import React, { useState, useRef, useEffect, useCallback } from "react";
import { useFormContext, useController } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { MapPin } from "lucide-react";

// Declare Google Maps types
declare global {
  interface Window {
    google: any;
  }
}

const google = (window as any).google;

type Props = {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  defaultvalue?: string;
  onAddressSelect?: (addressData: any) => void;
  zIndex?: number; // New prop for custom z-index (e.g., for modals)
  modifyValuesMapping?: any;
  [key: string]: any;
};

const RhfAddressAutoComplete = ({
  name,
  label,
  required = false,
  disabled = false,
  defaultvalue = "",
  placeholder = "Enter address...",
  onAddressSelect,
  modifyValuesMapping,
  zIndex = 1050, // Default to 1050 for Shadcn/Radix modals (above z-50)
  ...props
}: Props) => {
  const { control, setValue } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: defaultvalue,
    rules: required ? { required: true } : {},
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [refReady, setRefReady] = useState(false);
  const [googleReady, setGoogleReady] = useState(() =>
    typeof window !== "undefined" && !!window.google?.maps?.places
  );

  // Re-init autocomplete when Google Places script loads (script is async in main.tsx)
  useEffect(() => {
    if (googleReady) return;
    const onLoaded = () => setGoogleReady(true);
    window.addEventListener("google-maps-places-loaded", onLoaded);
    // In case script loaded before we subscribed
    if (window.google?.maps?.places) setGoogleReady(true);
    return () => window.removeEventListener("google-maps-places-loaded", onLoaded);
  }, [googleReady]);

  // Merge refs: both form controller and our ref need the input element (fixes registration + autocomplete init)
  const setRef = useCallback(
    (el: HTMLInputElement | null) => {
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
      field.ref(el);
      setRefReady(!!el);
    },
    [field.ref]
  );

  // Handle z-index and positioning for Google Places dropdown, optimized for modals like Shadcn Dialog
  useEffect(() => {
    if (typeof window.google === "undefined" || !window.google?.maps?.places) {
      return;
    }

    const setPacStyles = () => {
      const containers = document.querySelectorAll('.pac-container');
      containers.forEach((container) => {
        const el = container as HTMLElement;
        el.style.zIndex = `${zIndex}`; // Use prop for flexibility (e.g., pass 1050 or higher)
        el.style.position = 'fixed'; // Fixed to ensure it escapes stacking contexts in portaled modals
        el.style.pointerEvents = 'auto'; // Ensure clickable
        el.style.background = 'white'; // Fallback if theme issues
        // Optional: Add shadow/border if needed for visibility in dark mode
        el.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
      });
    };

    // Run initially
    setPacStyles();

    // MutationObserver for dynamic dropdowns
    const observer = new MutationObserver(() => {
      setTimeout(setPacStyles, 0); // Slight delay to ensure DOM is updated
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Also on focus/click to catch quick opens (run when ref is ready)
    const handleInputFocus = () => setTimeout(setPacStyles, 100);
    const inputEl = inputRef.current;
    if (inputEl) {
      inputEl.addEventListener('focus', handleInputFocus);
      inputEl.addEventListener('input', handleInputFocus);
    }

    // Global events for resize/scroll (modals can reposition)
    window.addEventListener('resize', setPacStyles);
    window.addEventListener('scroll', setPacStyles);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', setPacStyles);
      window.removeEventListener('scroll', setPacStyles);
      const inputElCleanup = inputRef.current;
      if (inputElCleanup) {
        inputElCleanup.removeEventListener('focus', handleInputFocus);
        inputElCleanup.removeEventListener('input', handleInputFocus);
      }
      // Cleanup styles
      const containers = document.querySelectorAll('.pac-container');
      containers.forEach((container) => {
        const el = container as HTMLElement;
        el.style.zIndex = '';
        el.style.position = '';
        el.style.pointerEvents = '';
        el.style.background = '';
        el.style.boxShadow = '';
      });
    };
  }, [zIndex, refReady]);

  useEffect(() => {
    if (!inputRef.current || disabled || !googleReady) return;

    // Check if Google Maps API is loaded
    if (typeof window.google === "undefined" || !window.google?.maps?.places) {
      return;
    }

    // To help with positioning in modals, restrict bounds to viewport (optional, but aids fixed modals)
    const bounds = new window.google.maps.LatLngBounds();
    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        types: ["address"],
        bounds, // Helps keep suggestions local/relevant
        strictBounds: false,
        fields: [
          "address_components",
          "formatted_address",
          "geometry",
          "name",
          "place_id",
        ],
      }
    );

    // Add place_changed listener
    const listener = autocompleteRef.current.addListener("place_changed", () => {
      setIsLoading(true);
      const place = autocompleteRef.current?.getPlace();
      if (!place || !place.address_components) {
        setIsLoading(false);
        return;
      }

      // Parse address components
      const addressData: any = {
        formatted_address: place.formatted_address || "",
        place_id: place.place_id || "",
        name: place.name || "",
        latitude: place.geometry?.location?.lat() || null,
        longitude: place.geometry?.location?.lng() || null,
      };

      // Extract address components
      place.address_components.forEach((component: any) => {
        const types = component.types;
        if (types.includes("street_number")) {
          addressData.street_number = component.long_name;
        }
        if (types.includes("route")) {
          addressData.street = component.long_name;
        }
        if (types.includes("locality")) {
          addressData.city = component.long_name;
        }
        if (types.includes("administrative_area_level_1")) {
          addressData.state = component.long_name;
          addressData.state_code = component.short_name;
        }
        if (types.includes("country")) {
          addressData.country = component.long_name;
          addressData.country_code = component.short_name;
        }
        if (types.includes("postal_code")) {
          addressData.zipcode = component.long_name;
        }
        if (types.includes("sublocality") || types.includes("sublocality_level_1")) {
          addressData.sublocality = component.long_name;
        }
        if (types.includes("neighborhood")) {
          addressData.neighborhood = component.long_name;
        }
        if (types.includes("premise")) {
          addressData.premise = component.long_name;
        }
        if (types.includes("postal_code_suffix")) {
          addressData.zipcode_suffix = component.long_name;
        }
      });

      // Construct address1 and address2
      addressData.address1 = [
        addressData.street_number,
        addressData.street,
      ].filter(Boolean).join(" ").trim() || "";

      addressData.address2 = [
        addressData.premise,
        addressData.sublocality,
        addressData.neighborhood,
      ].filter(Boolean).join(", ").trim() || "";

      // Set the formatted address in the field
      field.onChange(place.formatted_address || "");

      // Log the complete address object
      console.log("Selected Address Data:", addressData);

      // Call optional callback
         modifyValuesMapping && Object.entries(modifyValuesMapping).forEach(([key, value]) => {
        //   if(addressData?.[value as any]){
            setValue(key, addressData?.[value as any]);
        //   }
      })
      if (onAddressSelect) {
        onAddressSelect(addressData);
      }

      setIsLoading(false);
    });

    // Cleanup
    return () => {
      if (listener) {
        window.google.maps.event.removeListener(listener);
      }
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [disabled, field.onChange, refReady, googleReady]);

  // Prevent form submission on Enter when selecting from autocomplete
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <FormItem className="relative z-0"> {/* Boost local z for input container if needed */}
      {label && (
        <FormLabel>
          {label}
          {required ? "*" : ""}
        </FormLabel>
      )}
      <FormControl>
        <div className="relative">
          <Input
            {...field}
            ref={setRef}
            type="text"
            disabled={!!disabled}
            placeholder={placeholder}
            autoComplete="off"
            className="pr-10"
            onKeyPress={handleKeyPress} // Key fix: Prevent default on Enter after selection (on keypress)
            {...props}
          />
          <div className="absolute bottom-0 right-0 top-0 flex items-center pr-3 pointer-events-none">
            <MapPin
              size={18}
              className={`${isLoading ? "animate-pulse" : ""} text-gray-400`}
            />
          </div>
        </div>
      </FormControl>
      {error && <FormMessage>{error.message}</FormMessage>}
    </FormItem>
  );
};

export default RhfAddressAutoComplete;